import { useState } from 'react';
import { Check, Copy, KeyRound, Search, UserRound } from 'lucide-react';
import {
  isUzPhoneComplete,
  toUzPhone,
  uzNationalDigits,
  type StoreroomDto,
  type StoreroomWorkerDto,
  type WorkerPinResultDto,
} from '@eombor/contracts';
import { Button, Dialog, Input, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { Segmented } from '../../../shared/ui/Segmented.js';
import { PhoneInput } from '../../../shared/ui/PhoneInput.js';
import { useColleagues, useCreateWorker, useUpdateWorker } from '../model/storekeeping.queries.js';

export interface WorkerDialogProps {
  /** `null` — yangi xodim. */
  worker: StoreroomWorkerDto | null;
  /** Faqat mas'ul bo'lgan omborlar — boshqasiga biriktirib bo'lmaydi. */
  rooms: readonly StoreroomDto[];
  onClose: () => void;
  onPin: (result: WorkerPinResultDto) => void;
}

/**
 * XODIM OYNASI. Yaratishda ikki yo'l: kadrlar ro'yxatidan tanlash (ism va
 * lavozim o'zi to'ladi) yoki ro'yxatda yo'q odamni — vaqtinchalik ishchi,
 * pudratchi — qo'lda yozish. PIN server yaratadi va keyingi oynada BIR
 * MARTA ko'rsatiladi.
 */
export const WorkerDialog = ({ worker, rooms, onClose, onPin }: WorkerDialogProps) => {
  const editing = worker !== null;
  const [mode, setMode] = useState<'staff' | 'manual'>('staff');
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<string | null>(null);
  const [fullName, setFullName] = useState(worker?.fullName ?? '');
  const [position, setPosition] = useState(worker?.position ?? '');
  const [phone, setPhone] = useState(uzNationalDigits(worker?.phone ?? ''));
  const [roomIds, setRoomIds] = useState<string[]>(worker?.storeroomIds ?? (rooms[0] === undefined ? [] : [rooms[0].id]));
  const [active, setActive] = useState(worker?.isActive ?? true);

  const colleagues = useColleagues(search, !editing && mode === 'staff');
  const create = useCreateWorker();
  const update = useUpdateWorker();
  const busy = create.isPending || update.isPending;
  const error = create.error ?? update.error;

  /* Raqam MAJBURIY: mobil ilovaga kirishning yagona yo'li shu. Maydonda
     faqat milliy 9 xona turadi — `+998` uning yonida, o'zgarmas bo'lib
     qoladi (`PhoneInput`). */
  const phoneOk = isUzPhoneComplete(phone);
  const ready =
    roomIds.length > 0 &&
    phoneOk &&
    (editing || (mode === 'staff' ? picked !== null : fullName.trim().length >= 3));

  const submit = (): void => {
    if (!ready) return;
    if (editing) {
      update.mutate(
        {
          id: worker.id,
          input: {
            fullName: fullName.trim(),
            position: position.trim() === '' ? null : position.trim(),
            phone: toUzPhone(phone),
            storeroomIds: roomIds,
            isActive: active,
          },
        },
        { onSuccess: onClose },
      );
      return;
    }
    create.mutate(
      mode === 'staff' && picked !== null
        ? { userId: picked, phone: toUzPhone(phone), storeroomIds: roomIds }
        : {
            fullName: fullName.trim(),
            ...(position.trim() === '' ? {} : { position: position.trim() }),
            phone: toUzPhone(phone),
            storeroomIds: roomIds,
          },
      { onSuccess: onPin },
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={editing ? t('worker.editTitle') : t('worker.createTitle')}
      description={t('team.workersHint')}
      size="lg"
      busy={busy}
      onSubmit={submit}
      error={error === null ? undefined : error.message}
      footer={
        <div className="flex w-full justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {t('worker.cancel')}
          </Button>
          <Button loading={busy} disabled={!ready} onClick={submit}>
            {editing ? null : <KeyRound size={16} aria-hidden />}
            {editing ? t('worker.save') : t('worker.create')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {editing ? null : (
          <Segmented
            label={t('worker.createTitle')}
            value={mode}
            onChange={(next) => setMode(next as 'staff' | 'manual')}
            options={[
              { value: 'staff', label: t('worker.fromStaff') },
              { value: 'manual', label: t('worker.manual') },
            ]}
          />
        )}

        {!editing && mode === 'staff' ? (
          <div className="flex flex-col gap-2">
            <Input
              type="search"
              aria-label={t('worker.search')}
              placeholder={t('worker.search')}
              prefix={<Search size={16} />}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <ul role="listbox" aria-label={t('worker.search')} className="flex max-h-60 flex-col gap-1 overflow-y-auto">
              {(colleagues.data ?? []).length === 0 && !colleagues.isPending ? (
                <li className="px-2 py-3 text-sm text-text-muted">{t('worker.noColleagues')}</li>
              ) : null}
              {(colleagues.data ?? []).map((person) => {
                const selected = picked === person.userId;
                return (
                  <li key={person.userId}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => setPicked(person.userId)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-[var(--radius-md)] border px-3 py-2 text-left',
                        'focus-visible:outline-2 focus-visible:outline-accent',
                        selected ? 'border-accent bg-accent-soft' : 'border-transparent hover:bg-surface-2',
                      )}
                    >
                      <UserRound size={16} aria-hidden className="shrink-0 text-text-muted" />
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-sm font-medium text-text">{person.fullName}</span>
                        {person.positionName === null ? null : (
                          <span className="truncate text-xs text-text-muted">{person.positionName}</span>
                        )}
                      </span>
                      {selected ? <Check size={16} aria-hidden className="text-accent" /> : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label={t('worker.fullName')}
              maxLength={200}
              required
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
            />
            <Input
              label={t('worker.position')}
              maxLength={120}
              value={position}
              onChange={(event) => setPosition(event.target.value)}
            />
          </div>
        )}

        {/* RAQAM ikkala rejimda ham so'raladi: kadrlardan tanlangan xodimning
            ham telefoni bo'lishi kerak, aks holda ilovaga kira olmaydi. */}
        <PhoneInput
          label={t('worker.phone')}
          required
          placeholder={t('worker.phonePlaceholder')}
          hint={t('worker.phoneHint')}
          value={phone}
          onChange={setPhone}
        />

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-semibold text-text">{t('worker.storerooms')}</legend>
          <div className="flex flex-wrap gap-2">
            {rooms.map((room) => {
              const checked = roomIds.includes(room.id);
              return (
                <label
                  key={room.id}
                  className={cn(
                    'inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-sm',
                    'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-accent',
                    checked ? 'border-accent bg-accent-soft text-text' : 'border-border bg-surface text-text-muted',
                  )}
                >
                  <input
                    type="checkbox"
                    className="size-4 accent-[var(--color-accent)]"
                    checked={checked}
                    onChange={() =>
                      setRoomIds((prev) => (checked ? prev.filter((id) => id !== room.id) : [...prev, room.id]))
                    }
                  />
                  {room.name}
                </label>
              );
            })}
          </div>
        </fieldset>

        {editing ? (
          <label className="inline-flex items-center gap-2 text-sm text-text">
            <input
              type="checkbox"
              className="size-4 accent-[var(--color-accent)]"
              checked={active}
              onChange={(event) => setActive(event.target.checked)}
            />
            {t('worker.active')}
          </label>
        ) : null}
      </div>
    </Dialog>
  );
};

/** PIN — katta raqamlar, bir marta. Oyna yopilgach uni hech kim ko'ra olmaydi. */
export const PinRevealDialog = ({ result, onClose }: { result: WorkerPinResultDto; onClose: () => void }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('pin.title', { name: result.worker.fullName })}
      description={t('pin.hint')}
      footer={
        <div className="flex w-full justify-end">
          <Button onClick={onClose}>{t('pin.done')}</Button>
        </div>
      }
    >
      <div className="flex flex-col items-center gap-4 py-2">
        <output
          aria-label="PIN"
          className="tabular flex gap-2 font-mono text-4xl font-semibold tracking-[0.2em] text-text sm:text-5xl"
        >
          {[...result.pin].map((digit, index) => (
            <span
              key={index}
              className="flex h-16 w-11 items-center justify-center rounded-[var(--radius-md)] border border-border bg-surface-2 shadow-[var(--shadow-1)] sm:h-20 sm:w-14"
            >
              {digit}
            </span>
          ))}
        </output>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            void navigator.clipboard?.writeText(result.pin).then(() => setCopied(true));
          }}
        >
          {copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
          {copied ? t('pin.copied') : t('pin.copy')}
        </Button>
      </div>
    </Dialog>
  );
};
